import type { Dayjs } from 'dayjs';
import { WorkOrderStatus } from '@evimo/types/enums/maintenance-execution';
import type { KanbanTicketView, KanbanWorkOrderView } from './types';

/** Kanban columns shown on the default board (excludes ARCHIVED / CANCELLED). */
export const KANBAN_COLUMN_STATUSES = [
  WorkOrderStatus.PLANNED,
  WorkOrderStatus.ASSIGNED,
  WorkOrderStatus.IN_PROGRESS,
  WorkOrderStatus.ON_HOLD,
  WorkOrderStatus.COMPLETED,
  WorkOrderStatus.CLOSED,
] as const;

export type KanbanColumnStatus = (typeof KANBAN_COLUMN_STATUSES)[number];

export function isKanbanColumnStatus(
  status: string,
): status is KanbanColumnStatus {
  return (KANBAN_COLUMN_STATUSES as readonly string[]).includes(status);
}

export function columnDroppableId(status: KanbanColumnStatus): string {
  return `column:${status}`;
}

export function parseColumnDroppableId(id: string): KanbanColumnStatus | null {
  if (!id.startsWith('column:')) return null;
  const status = id.slice('column:'.length);
  return isKanbanColumnStatus(status) ? status : null;
}

export function workOrderDraggableId(workOrderId: string): string {
  return `wo:${workOrderId}`;
}

export function parseWorkOrderDraggableId(id: string): string | null {
  return id.startsWith('wo:') ? id.slice('wo:'.length) : null;
}

export function workOrderDroppableId(workOrderId: string): string {
  return `wo-drop:${workOrderId}`;
}

export function parseWorkOrderDroppableId(id: string): string | null {
  return id.startsWith('wo-drop:') ? id.slice('wo-drop:'.length) : null;
}

export function ticketDraggableId(ticketId: string): string {
  return `ticket:${ticketId}`;
}

export function parseTicketDraggableId(id: string): string | null {
  return id.startsWith('ticket:') ? id.slice('ticket:'.length) : null;
}

/**
 * Plan codes are long (`23-07-2026-eFeh6Zaf9kQG3HgB`); show the last segment for UI.
 * Full code stays available via tooltip / title attribute.
 */
export function shortPlanCode(planCode: string): string {
  const trimmed = planCode.trim();
  if (!trimmed) return trimmed;
  const parts = trimmed.split('-').filter(Boolean);
  if (parts.length <= 1) return trimmed;
  return parts[parts.length - 1]!;
}

/** Coerce OpenAPI `object | null` optional string fields. */
export function asOptionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

export function isCurativeTicketType(type: string): boolean {
  return type.trim().toUpperCase() === 'CURATIVE';
}

const INCIDENT_CODE_IN_TITLE = /\b(INC-\d{2}-\d{2}-\d+)\b/i;

/** Fallback when API did not return incidentCode but the ticket title mentions one. */
export function parseIncidentCodeFromTicketTitle(title: string): string | null {
  const match = title.trim().match(INCIDENT_CODE_IN_TITLE);
  return match?.[1] ?? null;
}

/** Like asOptionalString but coerces OpenAPI "object" fields that arrive as strings at runtime. */
export function coerceOptionalString(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (value == null) {
    return null;
  }
  const asString = String(value).trim();
  return asString.length > 0 ? asString : null;
}

/**
 * True when both site ids are present and differ.
 * Missing ids → no warning (allow move).
 */
export function isCrossSiteWorkOrderMove(
  sourceSiteId: string | null | undefined,
  targetSiteId: string | null | undefined,
): boolean {
  if (!sourceSiteId || !targetSiteId) return false;
  return sourceSiteId !== targetSiteId;
}

export type WorkOrderColumnTransition =
  | { kind: 'assign' }
  | { kind: 'start' }
  | { kind: 'hold' }
  | { kind: 'complete' }
  | { kind: 'closeWorkOrder' }
  | { kind: 'invalid' };

/** Completed visit ready for Closed when every ticket is terminal and not flagged for follow-up. */
export function isWorkOrderEligibleToClose(
  workOrder: Pick<KanbanWorkOrderView, 'status' | 'tickets'>,
): boolean {
  if (workOrder.status !== WorkOrderStatus.COMPLETED) {
    return false;
  }
  if (workOrder.tickets.length === 0) {
    return false;
  }
  return workOrder.tickets.every(
    (ticket) =>
      !ticket.needsFollowUp &&
      (ticket.status === 'CLOSED' || ticket.status === 'CANCELLED'),
  );
}

/** Qualify (close intervention) from a completed visit — Kanban button or drag to Closed. */
export function canQualifyInterventionOnKanban(
  workOrder: Pick<KanbanWorkOrderView, 'status'>,
  ticket: Pick<
    KanbanTicketView,
    'status' | 'needsFollowUp' | 'lastExecutionResult'
  >,
): boolean {
  if (workOrder.status !== WorkOrderStatus.COMPLETED) {
    return false;
  }
  if (ticket.needsFollowUp) {
    return false;
  }
  if (ticket.status === 'CLOSED' || ticket.status === 'CANCELLED') {
    return false;
  }
  return ticket.lastExecutionResult === 'COMPLETED_OK';
}

/**
 * Maps a Kanban column drop to the backend command that advances WO status.
 */
export function resolveColumnTransition(
  from: WorkOrderStatus,
  to: KanbanColumnStatus,
): WorkOrderColumnTransition {
  if (from === to) return { kind: 'invalid' };

  if (to === WorkOrderStatus.ASSIGNED) {
    if (from === WorkOrderStatus.PLANNED || from === WorkOrderStatus.ON_HOLD) {
      return { kind: 'assign' };
    }
    return { kind: 'invalid' };
  }

  if (to === WorkOrderStatus.IN_PROGRESS) {
    if (from === WorkOrderStatus.ASSIGNED || from === WorkOrderStatus.ON_HOLD) {
      return { kind: 'start' };
    }
    return { kind: 'invalid' };
  }

  if (to === WorkOrderStatus.ON_HOLD) {
    if (from === WorkOrderStatus.IN_PROGRESS) {
      return { kind: 'hold' };
    }
    return { kind: 'invalid' };
  }

  if (to === WorkOrderStatus.COMPLETED) {
    if (
      from === WorkOrderStatus.IN_PROGRESS ||
      from === WorkOrderStatus.ON_HOLD
    ) {
      return { kind: 'complete' };
    }
    return { kind: 'invalid' };
  }

  if (to === WorkOrderStatus.CLOSED) {
    if (from === WorkOrderStatus.COMPLETED) {
      return { kind: 'closeWorkOrder' };
    }
    return { kind: 'invalid' };
  }

  return { kind: 'invalid' };
}

export function isTicketMoveTargetStatus(status: WorkOrderStatus): boolean {
  return (
    status === WorkOrderStatus.PLANNED ||
    status === WorkOrderStatus.ASSIGNED ||
    status === WorkOrderStatus.IN_PROGRESS ||
    status === WorkOrderStatus.ON_HOLD
  );
}

export function priorityTagColor(
  priority: string,
): 'red' | 'orange' | 'blue' | 'default' {
  switch (priority) {
    case 'HIGH':
      return 'red';
    case 'MEDIUM':
      return 'orange';
    case 'LOW':
      return 'blue';
    default:
      return 'default';
  }
}

export function ticketTypeTagColor(type: string): 'blue' | 'red' | 'default' {
  switch (type) {
    case 'PREVENTIVE':
      return 'blue';
    case 'CURATIVE':
      return 'red';
    default:
      return 'default';
  }
}

export function ticketStatusTagColor(
  status: string,
): 'blue' | 'green' | 'default' | 'red' {
  switch (status) {
    case 'OPEN':
      return 'blue';
    case 'IN_PROGRESS':
      return 'green';
    case 'CLOSED':
      return 'default';
    case 'CANCELLED':
      return 'red';
    default:
      return 'default';
  }
}

export type TicketDueUrgency = 'overdue' | 'soon' | 'normal';

export function ticketDueUrgency(
  dueAt: Date | string | null | undefined,
  now = new Date(),
): TicketDueUrgency | null {
  if (!dueAt) return null;
  const due = dueAt instanceof Date ? dueAt : new Date(dueAt);
  if (Number.isNaN(due.getTime())) return null;

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfDue = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffDays = Math.round(
    (startOfDue.getTime() - startOfToday.getTime()) / (24 * 60 * 60 * 1000),
  );

  if (diffDays < 0) return 'overdue';
  if (diffDays <= 3) return 'soon';
  return 'normal';
}

export function formatTicketDueDate(
  dueAt: Date | string | null | undefined,
): string | null {
  if (!dueAt) return null;
  const due = dueAt instanceof Date ? dueAt : new Date(dueAt);
  if (Number.isNaN(due.getTime())) return null;
  return due.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ticketDueColor(urgency: TicketDueUrgency): string {
  switch (urgency) {
    case 'overdue':
      return '#b91c1c';
    case 'soon':
      return '#c2410c';
    default:
      return '#334155';
  }
}

/** Sentinel Select value for work orders with no assigned technician. */
export const UNASSIGNED_TECHNICIAN_FILTER = '__unassigned__';

export type KanbanBoardFilters = {
  siteId: string;
  technicianId: string;
  planId: string;
  followUpOnly: boolean;
  /** Inclusive calendar-day range; null means no bound. */
  dueFrom: Dayjs | null;
  dueTo: Dayjs | null;
};

export type PlanFilterOption = {
  value: string;
  label: string;
};

function startOfLocalDay(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
}

function endOfLocalDay(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  ).getTime();
}

export function ticketDueAtInRange(
  dueAt: Date | null | undefined,
  from: KanbanBoardFilters['dueFrom'],
  to: KanbanBoardFilters['dueTo'],
): boolean {
  if (!from && !to) return true;
  if (!dueAt) return false;
  const due = dueAt instanceof Date ? dueAt : new Date(dueAt);
  if (Number.isNaN(due.getTime())) return false;
  const dueMs = due.getTime();
  if (from && dueMs < startOfLocalDay(from.toDate())) return false;
  if (to && dueMs > endOfLocalDay(to.toDate())) return false;
  return true;
}

export function workOrderMatchesKanbanFilters(
  workOrder: KanbanWorkOrderView,
  filters: KanbanBoardFilters,
): boolean {
  if (filters.siteId && workOrder.siteId !== filters.siteId) return false;

  if (filters.technicianId === UNASSIGNED_TECHNICIAN_FILTER) {
    if (workOrder.assignedTechnicianId != null) return false;
  } else if (
    filters.technicianId &&
    workOrder.assignedTechnicianId !== filters.technicianId
  ) {
    return false;
  }

  if (
    filters.followUpOnly &&
    !workOrder.tickets.some((ticket) => ticket.needsFollowUp)
  ) {
    return false;
  }

  if (filters.planId) {
    if (
      !workOrder.tickets.some(
        (ticket) => ticket.preventivePlanId === filters.planId,
      )
    ) {
      return false;
    }
  }

  if (filters.dueFrom || filters.dueTo) {
    if (
      !workOrder.tickets.some((ticket) =>
        ticketDueAtInRange(ticket.dueAt, filters.dueFrom, filters.dueTo),
      )
    ) {
      return false;
    }
  }

  return true;
}

export function buildPlanFilterOptions(
  workOrders: KanbanWorkOrderView[],
): PlanFilterOption[] {
  const byId = new Map<string, PlanFilterOption>();
  for (const workOrder of workOrders) {
    for (const ticket of workOrder.tickets) {
      if (!ticket.preventivePlanId || byId.has(ticket.preventivePlanId)) {
        continue;
      }
      const name = ticket.planName?.trim();
      const code = ticket.planCode?.trim();
      const shortCode = code ? shortPlanCode(code) : undefined;
      const label = name
        ? shortCode
          ? `${name} (${shortCode})`
          : name
        : (shortCode ?? code ?? ticket.preventivePlanId);
      byId.set(ticket.preventivePlanId, {
        value: ticket.preventivePlanId,
        label,
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** How work-order cards are ordered inside each kanban column. */
export const KANBAN_SORT_OPTIONS = [
  'updated_desc',
  'updated_asc',
  'created_desc',
  'created_asc',
  'priority_desc',
  'priority_asc',
  'due_asc',
  'due_desc',
] as const;

export type KanbanSortOption = (typeof KANBAN_SORT_OPTIONS)[number];

export const KANBAN_SORT_GROUPS = [
  'updated',
  'created',
  'priority',
  'due',
] as const;

export type KanbanSortGroup = (typeof KANBAN_SORT_GROUPS)[number];

export const KANBAN_SORT_OPTIONS_BY_GROUP: Record<
  KanbanSortGroup,
  readonly KanbanSortOption[]
> = {
  updated: ['updated_desc', 'updated_asc'],
  created: ['created_desc', 'created_asc'],
  priority: ['priority_desc', 'priority_asc'],
  due: ['due_asc', 'due_desc'],
};

export const DEFAULT_KANBAN_SORT: KanbanSortOption = 'updated_desc';

export const KANBAN_SORT_STORAGE_KEY = 'evimo.workOrders.kanban.sort';

const PRIORITY_RANK: Record<string, number> = {
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

export function isKanbanSortOption(value: string): value is KanbanSortOption {
  return (KANBAN_SORT_OPTIONS as readonly string[]).includes(value);
}

export function readStoredKanbanSort(
  storage:
    | Pick<Storage, 'getItem'>
    | null
    | undefined = globalThis.localStorage,
): KanbanSortOption {
  try {
    const raw = storage?.getItem(KANBAN_SORT_STORAGE_KEY);
    if (raw && isKanbanSortOption(raw)) return raw;
  } catch {
    // Ignore quota / privacy mode errors.
  }
  return DEFAULT_KANBAN_SORT;
}

export function writeStoredKanbanSort(
  value: KanbanSortOption,
  storage:
    | Pick<Storage, 'setItem'>
    | null
    | undefined = globalThis.localStorage,
): void {
  try {
    storage?.setItem(KANBAN_SORT_STORAGE_KEY, value);
  } catch {
    // Ignore quota / privacy mode errors.
  }
}

function earliestTicketDueMs(workOrder: KanbanWorkOrderView): number | null {
  let earliest: number | null = null;
  for (const ticket of workOrder.tickets) {
    if (!ticket.dueAt) continue;
    const ms = ticket.dueAt.getTime();
    if (Number.isNaN(ms)) continue;
    if (earliest == null || ms < earliest) earliest = ms;
  }
  return earliest;
}

function compareByCode(a: KanbanWorkOrderView, b: KanbanWorkOrderView): number {
  return a.code.localeCompare(b.code);
}

/** Last meaningful activity — prefer completion time when the WO is done. */
function activityMs(workOrder: KanbanWorkOrderView): number {
  const updated = workOrder.updatedAt.getTime();
  const completed = workOrder.completedAt?.getTime();
  if (completed != null && !Number.isNaN(completed)) {
    return Math.max(updated, completed);
  }
  return updated;
}

function compareNullableMs(
  aMs: number | null,
  bMs: number | null,
  direction: 'asc' | 'desc',
): number {
  if (aMs == null && bMs == null) return 0;
  // Missing dates always sort last, regardless of direction.
  if (aMs == null) return 1;
  if (bMs == null) return -1;
  return direction === 'asc' ? aMs - bMs : bMs - aMs;
}

/**
 * Sorts WO cards for a column. Tie-break is always WO code A→Z.
 * Missing due dates sort last for both due directions.
 * "Updated" uses completedAt when set (just-finished WOs rise to the top).
 * Imported external WOs (Monprojet OT) always sort before native Evimo WOs
 * so freshly retrieved interventions stay at the front of each column.
 */
export function sortKanbanWorkOrders(
  workOrders: KanbanWorkOrderView[],
  sort: KanbanSortOption,
): KanbanWorkOrderView[] {
  const ranked = [...workOrders];
  ranked.sort((a, b) => {
    const importedDelta =
      Number(Boolean(b.externalIdentifier?.trim())) -
      Number(Boolean(a.externalIdentifier?.trim()));
    if (importedDelta !== 0) return importedDelta;

    let primary = 0;
    switch (sort) {
      case 'updated_desc':
        primary = activityMs(b) - activityMs(a);
        break;
      case 'updated_asc':
        primary = activityMs(a) - activityMs(b);
        break;
      case 'created_desc':
        primary = b.createdAt.getTime() - a.createdAt.getTime();
        break;
      case 'created_asc':
        primary = a.createdAt.getTime() - b.createdAt.getTime();
        break;
      case 'priority_desc': {
        const aRank = PRIORITY_RANK[a.highestPriority] ?? 0;
        const bRank = PRIORITY_RANK[b.highestPriority] ?? 0;
        primary = bRank - aRank;
        break;
      }
      case 'priority_asc': {
        const aRank = PRIORITY_RANK[a.highestPriority] ?? 0;
        const bRank = PRIORITY_RANK[b.highestPriority] ?? 0;
        primary = aRank - bRank;
        break;
      }
      case 'due_asc':
        primary = compareNullableMs(
          earliestTicketDueMs(a),
          earliestTicketDueMs(b),
          'asc',
        );
        break;
      case 'due_desc':
        primary = compareNullableMs(
          earliestTicketDueMs(a),
          earliestTicketDueMs(b),
          'desc',
        );
        break;
      default:
        primary = 0;
    }
    return primary !== 0 ? primary : compareByCode(a, b);
  });
  return ranked;
}
